import React from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard, GoldPillButton } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { Layers, Terminal, Sparkles, Network, ArrowRight } from "lucide-react";
import { BOOKING_URL } from "@/mock";

export default function CustomSoftware() {
  return (
    <Layout>
      <SEO
        title="Custom Software Systems"
        description="High-performance full-stack software development. API optimization, database refactoring, and microservices engineered for speed, offline-first compliance, and low network bandwidth."
        url="/services/custom-software"
      />
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Header Hero */}
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--utu-gold))]" />
            Full-Stack Systems Engineering
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl mb-6">
            Custom Software Systems
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            "You describe it, we build it." High-fidelity, custom software systems engineered for absolute reliability, security compliance, and low-latency network performance.
          </p>
        </div>

        {/* Core Pillars */}
        <div className="grid gap-6 md:grid-cols-2 mb-16">
          <GlassCard className="p-8">
            <Layers className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Legacy Refactoring & Modernization</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              We untangle heavy monolithic applications and rebuild them into clean, modular, and maintainable services. Keep your transaction histories intact while upgrading to modern, responsive frontends and APIs.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Network className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">High-Concurrency API Design</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              We design and implement RESTful and GraphQL endpoints using FastAPI and Django. Our architectures are structured to process high concurrent request volumes without database deadlocks or latency bloat.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Terminal className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Offline-First & Low-Bandwidth</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              Operating across African urban centers requires resilient design. We build applications incorporating client-side local cache states (using IndexDB or local persistence layers) to prevent data loss when cellular signals drop.
            </p>
          </GlassCard>

          <GlassCard className="p-8">
            <Sparkles className="h-8 w-8 text-[hsl(var(--utu-gold))] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Robust Security & ACID Compliance</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              Every system is constructed under zero-trust guidelines. We enforce strict database normalization, row-level security constraints, and complete encryption protocols for data at rest and in transit.
            </p>
          </GlassCard>
        </div>

        {/* Process Steps */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Development Rigor</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="p-6 border border-white/5 bg-white/[0.02] rounded-xl">
              <div className="text-2xl font-bold text-[hsl(var(--utu-gold))] mb-2">01</div>
              <h4 className="font-semibold text-white mb-1">Architecture Blueprint</h4>
              <p className="text-xs text-white/60">We map the data structures, system flows, and API boundaries before writing a single line of production code.</p>
            </div>
            <div className="p-6 border border-white/5 bg-white/[0.02] rounded-xl">
              <div className="text-2xl font-bold text-[hsl(var(--utu-gold))] mb-2">02</div>
              <h4 className="font-semibold text-white mb-1">Milestone Sprints</h4>
              <p className="text-xs text-white/60">Incremental releases on private staging domains, backed by automated unit tests and code validation routines.</p>
            </div>
            <div className="p-6 border border-white/5 bg-white/[0.02] rounded-xl">
              <div className="text-2xl font-bold text-[hsl(var(--utu-gold))] mb-2">03</div>
              <h4 className="font-semibold text-white mb-1">CI/CD Deployment</h4>
              <p className="text-xs text-white/60">Clean dockerized builds deployed with robust orchestrations (Docker Compose or Kubernetes) and health-monitoring setups.</p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Have a System in Mind?</h3>
          <p className="text-sm text-white/60 mb-8 max-w-xl mx-auto">
            Get in touch to detail your custom platform needs. We provide fixed-scope milestones and clear, actionable deliverables.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <GoldPillButton className="h-12 px-8">
              Discuss Your Project
              <ArrowRight className="h-4 w-4 ml-2" />
            </GoldPillButton>
          </a>
        </div>
      </section>
    </Layout>
  );
}
